// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect, type ReactNode } from 'react'
import { HashRouter as Router, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster, DebugIndicator } from '@/components/ui'
import AppErrorBoundary from './app-shell/components/AppErrorBoundary'
import Layout from './app-shell/components/Layout'
import AccessDenied from './app-shell/components/AccessDenied'
import PipelinesPage from './app-shell/pages/PipelinesPage'
import LayoutPage from './app-shell/pages/LayoutPage'
import Jobs from './app-shell/pages/Jobs'
import Costs from './app-shell/pages/Costs'
import Metrics from './app-shell/pages/Metrics'
import DatasetsPage from './app-shell/pages/DatasetsPage'
import SchemasPage from './app-shell/pages/SchemasPage'
import KnowledgeBasesPage from './app-shell/pages/KnowledgeBasesPage'
import LLMConfigPage from './app-shell/pages/LLMConfigPage'
import MigrationsPage from './app-shell/pages/MigrationsPage'
import DataRestorePage from './app-shell/pages/DataRestorePage'
import SystemPromptsPage from './app-shell/pages/SystemPromptsPage'
import ScheduledJobsPage from './app-shell/pages/ScheduledJobsPage'
import ConnectorsPage from './app-shell/pages/ConnectorsPage'
import WorkspaceConnectorsPage from './app-shell/pages/WorkspaceConnectorsPage'
import OrgManagementPage from './app-shell/pages/OrgManagementPage'
import ApplicationsAdminPage from './app-shell/pages/ApplicationsAdminPage'
import SiteBannerPage from './app-shell/pages/SiteBannerPage'
import AgentsPage from './app-shell/pages/AgentsPage'
import AgentLibraryPage from './app-shell/pages/AgentLibraryPage'
import AgentTemplateSettingsPage from './app-shell/pages/AgentTemplateSettingsPage'
import NewAgentPage from './app-shell/pages/NewAgentPage'
import { AuthProvider, useAuth, initializeWorkspacesApi } from '@/subsystems/workspaces'
import { useUserProfile } from '@/subsystems/workspaces/hooks/use-user-profile'
import { initializeJobsApi } from '@/subsystems/jobs'
import { RouteGuard, isGlobalAdmin, isGlobalOrWorkspaceAdmin, canManageUsers } from './app-shell/components/RouteGuard'
import WorkspaceAdminPage from './app-shell/pages/WorkspaceAdminPage'
import WorkspaceSettingsPage from './app-shell/pages/WorkspaceSettingsPage'
import { VaultPage } from './subsystems/vault/pages/VaultPage'
import UsersPage from './app-shell/pages/UsersPage'
import DocumentDashboardContractsPrintPage from './app-shell/pages/DocumentDashboardContractsPrintPage'
import { apiClient } from './app-shell/api/api-client'
import { setImpersonationTarget } from './subsystems/workspaces/services/impersonation-session'
import { CASAuthProvider, useCASAuthContext } from '@umd-dit-ai-workspace/cas-auth'
import { initializeApiClient } from '@/core'
import "@/app-shell/styles/app.css"

initializeApiClient(apiClient)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 5000,
      refetchOnWindowFocus: false,
      placeholderData: (previousData: unknown) => previousData,
      throwOnError: false,
    },
    mutations: {
      retry: false
    }
  }
})


// Routing component
function WorkspaceAwareRouting() {
  const {
    isAuthenticated,
    isLoading,
    hasWorkspaceAccess,
    selectedOrganizationId,
  } = useAuth();
  // A connector grant unlocks the normal Connectors page — no separate
  // surface. Workspace data and every other page stay membership-gated.
  const { data: userProfile } = useUserProfile(isAuthenticated);
  const connectorGrants = userProfile?.connectorAdminGrants ?? [];
  const hasConnectorGrantInOrg = connectorGrants.some(
    grant => grant.orgId === selectedOrganizationId,
  );

  // Only gate on initial auth load. Don't gate on loadingWorkspaces — that goes
  // true on every org switch, and unmounting Layout mid-switch loses Layout's
  // deepLinkApplied ref, causing the deep-link effect to revert the user's
  // selection back to the (stale) URL on remount.
  if (isLoading) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-xl font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Render routes with security guards - Layout wraps all routes
  return (
    <Routes>
      <Route path="/print/document-dashboard-contracts" element={<DocumentDashboardContractsPrintPage />} />
      <Route path="/" element={<Layout/>}>
        {/* If authenticated but no workspace access, show access denied within layout */}
        {isAuthenticated && !hasWorkspaceAccess ? (
          <Route index element={<AccessDenied />} />
        ) : (
          <Route index element={null} /> /* Dashboard handled by Layout */
        )}
        <Route path="pipelines" element={<RouteGuard allow={isGlobalAdmin}><PipelinesPage/></RouteGuard>}/>
        <Route path="layout" element={<RouteGuard allow={isGlobalAdmin}><LayoutPage/></RouteGuard>}/>
        <Route path="jobs" element={<Jobs/>}/>
        <Route path="costs" element={<RouteGuard allow={isGlobalOrWorkspaceAdmin}><Costs/></RouteGuard>}/>
        <Route path="metrics" element={<RouteGuard allow={isGlobalAdmin}><Metrics/></RouteGuard>}/>
        <Route path="datasets" element={<DatasetsPage/>}/>
        <Route path="schemas" element={<SchemasPage/>}/>
        <Route path="knowledge-bases" element={<RouteGuard allow={isGlobalAdmin}><KnowledgeBasesPage/></RouteGuard>}/>
        <Route path="llm-config" element={<RouteGuard allow={isGlobalAdmin}><LLMConfigPage/></RouteGuard>}/>
        <Route path="migrations" element={<RouteGuard allow={isGlobalAdmin}><MigrationsPage/></RouteGuard>}/>
        <Route path="data-restore" element={<RouteGuard allow={isGlobalAdmin}><DataRestorePage/></RouteGuard>}/>
        <Route path="system-prompts" element={<RouteGuard allow={isGlobalAdmin}><SystemPromptsPage/></RouteGuard>}/>
        <Route path="scheduled-jobs" element={<RouteGuard allow={isGlobalAdmin}><ScheduledJobsPage/></RouteGuard>}/>
        <Route path="connectors" element={<RouteGuard allow={isGlobalAdmin}><ConnectorsPage/></RouteGuard>}/>
        <Route
          path="workspace-connectors"
          element={
            <RouteGuard allow={a => isGlobalOrWorkspaceAdmin(a) || hasConnectorGrantInOrg}>
              <WorkspaceConnectorsPage/>
            </RouteGuard>
          }
        />
        <Route path="orgs" element={<RouteGuard allow={isGlobalAdmin}><OrgManagementPage/></RouteGuard>}/>
        <Route path="applications" element={<RouteGuard allow={isGlobalAdmin}><ApplicationsAdminPage/></RouteGuard>}/>
        <Route path="site-banner" element={<RouteGuard allow={isGlobalAdmin}><SiteBannerPage/></RouteGuard>}/>
        <Route path="agents/new" element={<RouteGuard allow={isGlobalAdmin}><NewAgentPage/></RouteGuard>}/>
        <Route path="agents/:agentId" element={<RouteGuard allow={isGlobalOrWorkspaceAdmin}><AgentsPage/></RouteGuard>}/>
        <Route path="agent-library" element={<RouteGuard allow={isGlobalAdmin}><AgentLibraryPage/></RouteGuard>}/>
        <Route path="agent-library/:agentId" element={<RouteGuard allow={isGlobalAdmin}><AgentTemplateSettingsPage/></RouteGuard>}/>
        <Route path="workspace-admin" element={<RouteGuard allow={isGlobalOrWorkspaceAdmin}><WorkspaceAdminPage/></RouteGuard>}/>
        <Route path="workspace-settings" element={<RouteGuard allow={isGlobalOrWorkspaceAdmin}><WorkspaceSettingsPage/></RouteGuard>}/>
        <Route path="vault" element={<VaultPage/>}/>
        <Route path="users" element={<RouteGuard allow={canManageUsers}><UsersPage/></RouteGuard>}/>
      </Route>
    </Routes>
  );
}

// Bridges CAS auth logout to workspaces AuthProvider
function AuthProviderWithLogout({ children }: { children: ReactNode }) {
  useCASAuthContext();

  const logout = () => {
    console.log('[App] Clearing DocReview auth session');
    setImpersonationTarget(null);
    localStorage.removeItem('cas_jwt');
    localStorage.removeItem('cas_user');
    localStorage.removeItem('cas_redirect_path');
    localStorage.removeItem('cas_redirect_count');

    const url = new URL(window.location.href);
    url.searchParams.delete('ticket');
    window.history.replaceState({}, document.title, url.toString());

    // Force app-local re-auth without logging out of central CAS.
    window.location.reload();
  };
  return (
    <AuthProvider appName="docreview" logout={logout}>
      {children}
    </AuthProvider>
  );
}

function App() {
  useEffect(() => {
    // Initialize Jobs API with our API client
    initializeJobsApi(apiClient.getAxiosInstance());

    // Initialize Workspaces API with our API client
    initializeWorkspacesApi(apiClient.getAxiosInstance());

    // Check for debug flag in URL on initial load
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') {
      console.log('[App] Debug mode activated via URL parameter');
      sessionStorage.setItem('debugModeEnabled', 'true');
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  console.log('[App] CAS Configuration:', {
    casLoginUrl: import.meta.env.VITE_CAS_LOGIN_URL,
    serviceUrl: import.meta.env.VITE_CAS_SERVICE_URL,
    apiGatewayUrl: import.meta.env.VITE_CAS_API_URL,
  });

  return (
    <QueryClientProvider client={queryClient}>
      <CASAuthProvider
        config={{
          // Use ?? to preserve empty string (disables CAS redirect for local dev)
          casLoginUrl: import.meta.env.VITE_CAS_LOGIN_URL ?? 'https://login.umd.edu/cas/login',
          serviceUrl: import.meta.env.VITE_CAS_SERVICE_URL ?? window.location.origin,
          apiGatewayUrl: import.meta.env.VITE_CAS_API_URL ?? import.meta.env.VITE_API_URL + '/auth/cas/login',
          debug: import.meta.env.VITE_DEBUG_MODE === 'true',
        }}
        onAccessGranted={(authenticated: boolean, user) => {
          console.log('[App] CAS access granted:', { authenticated, user });
        }}
        onAccessDenied={() => {
          console.log('[App] CAS access denied');
        }}
      >
        <Router>
          <AppErrorBoundary>
            <AuthProviderWithLogout>
              <WorkspaceAwareRouting />
              <Toaster />
              <DebugIndicator />
            </AuthProviderWithLogout>
          </AppErrorBoundary>
        </Router>
      </CASAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
